<script setup>
import {computed} from "vue";
import {useSeoScore} from "@/composables/useSeoScore";
import {startPostAnimationEffects} from "@/components/seo-score/visibility-chart/composables/animation";
import {kitName} from "@/composables/useLocalDomains";

const {userName, hasSeoScoreFeature} = defineProps({
  userName: {
    type: String,
    required: true,
  },
  hasSeoScoreFeature: {
    type: Boolean,
    default: false,
  }
});

const {visibilityScoreStats} = useSeoScore();

const textOptions = [
  { upTo: 12,  text: () => "Welcome to base camp. This is where your climb starts..." },
  { upTo: 27,  text: () => "Your visibility is building. Keep going..." },
  { upTo: 33,  text: (score) => `You're ${34 - score} points from moving up...` },
  { upTo: 49,  text: () => "You’re on your way up..." },
  { upTo: 60,  text: () => `You've got momentum now. ${kitName.value}'s work is compounding...` },
  { upTo: 66,  text: (score) => `You're ${67 - score} points from trailblazer...` },
  { upTo: 82,  text: () => "You're trailblazing. Customers are more likely to find you first" },
  { upTo: 93,  text: () => "From here, everything looks better - more searches, more clicks, more calls" },
  { upTo: 99,  text: () => "You're a business customers find first" },
  { upTo: 100, text: () => "Enjoy the view from the top. Ready to go again?" },
];

const scoreText = computed(() => {
  if (!hasSeoScoreFeature || !startPostAnimationEffects.value) {
    return null;
  }

  const rawScore = visibilityScoreStats.value?.score;
  if (rawScore == null) {
    return null;
  }

  const score = Math.round(rawScore * 100);
  return textOptions.find(({ upTo }) => score <= upTo)?.text(score) ?? null;
});

</script>

<template>
  <div>
    <div class="tw-relative tw-h-9 md:tw-h-10 tw-overflow-hidden">
      <Transition
        enter-active-class="tw-transition tw-duration-700 tw-ease-out"
        leave-active-class="tw-transition tw-duration-500 tw-ease-in tw-absolute tw-inset-x-0"
        enter-from-class="tw-opacity-0 tw-translate-y-full"
        leave-to-class="tw-opacity-0 -tw-translate-y-full"
      >
        <h1
          :key="scoreText"
          class="tw-text-neutral-900 tw-text-3xl md:tw-text-4.5xl tw-font-bold"
        >
          {{ scoreText ?? `Welcome back, ${userName}` }}
        </h1>
      </Transition>
    </div>
  </div>
</template>
